// The license + rollout gate for the Smart CR pages: the smart-cr license
// module plus the smart-cr PostHog flag (FF_SMART_CR without PostHog). Smart
// CR pages are deep-linkable (a saved draft or record link, the agent
// hand-off card), so deep links must enforce it too: a closed gate redirects
// home, but only once both the license query and PostHog have answered,
// since the gate is fail-closed while they load. Same shape as
// EdgeConnectorsGate.

import React, { ReactNode, useEffect } from "react";
import { useHistory } from "react-router-dom";

import { useSmartCrGate } from "src/helpers/licensedRolloutGates";
import { useGetUserDetails } from "src/helpers/UserDetails";
import { useIsAdmin } from "src/helpers/useIsAdmin";

const SmartCrGate = ({ children }: { children: ReactNode }) => {
  const history = useHistory();
  const { enabled, loaded } = useSmartCrGate();

  useEffect(() => {
    if (loaded && !enabled) history.replace("/");
  }, [loaded, enabled, history]);

  return enabled ? <>{children}</> : null;
};

/** Wraps a Smart CR route component in SmartCrGate (src/routes.jsx). */
export function withSmartCrGate<P extends object>(
  Component: React.ComponentType<P>,
): React.ComponentType<P> {
  const Gated = (props: P) => (
    <SmartCrGate>
      <Component {...props} />
    </SmartCrGate>
  );
  Gated.displayName = `withSmartCrGate(${
    Component.displayName || Component.name || "Component"
  })`;
  return Gated;
}

/**
 * Platform admins only: the Configuration pages change the plant's form,
 * lifecycle and routing for everyone, and their API routes already refuse
 * anyone else. A signed-in non-admin who deep-links there lands on the
 * Smart CR workspace instead; nothing renders until the user is known.
 */
export const SmartCrAdminGate = ({ children }: { children: ReactNode }) => {
  const history = useHistory();
  const user = useGetUserDetails();
  const isAdmin = useIsAdmin();
  useEffect(() => {
    if (user && !isAdmin) history.replace("/products/cap-ai/cr");
  }, [user, isAdmin, history]);
  return isAdmin ? <>{children}</> : null;
};

/** Wraps a configuration route in both gates (src/routes.jsx). */
export function withSmartCrAdminGate<P extends object>(
  Component: React.ComponentType<P>,
): React.ComponentType<P> {
  const Gated = (props: P) => (
    <SmartCrGate>
      <SmartCrAdminGate>
        <Component {...props} />
      </SmartCrAdminGate>
    </SmartCrGate>
  );
  Gated.displayName = `withSmartCrAdminGate(${
    Component.displayName || Component.name || "Component"
  })`;
  return Gated;
}

export default SmartCrGate;
